import Promise from 'bluebird';
import { some, pick } from 'underscore';
import { getLanguage, getDefaultLanguage, languageParseMode } from './countdown-language';
import { defaultCountdownDays } from './utils';

/**
 * Task queue for getting/setting locales and substituting countdown rule
 *   definitions with their evaluated values
 */
let countdownEvalTaskChain = Promise.resolve('');

/*
 * Captures:
 * [0]: whole match
 * [1]: function name
 * [2]: date
 * [3]: time (may be undefined)
 * [4]: locale (may be undefined)
 * [5]: number of days to countdown from (may be undefined)
 */
const countdownRuleSyntax = /\{=\s*(global_countdown|countdown)\s*\(\s*"(?:(\d{4}\/\d{1,2}\/\d{1,2})(?:\s((?:[0-1][0-9]|2[0-3]):(?:[0-5][0-9]):(?:[0-5][0-9]))|))"\s*(?:,\s*"([a-zA-Z]{2}(?:-[a-zA-Z\d]{2,3}|))"\s*(?:,\s*([1-9]\d{0,2})|)|)\s*\)\s*\}/i;

/*
 * Captures:
 * [0]: whole match
 * [1]: function name
 * [2]: date
 * [3]: time (may be undefined)
 * [4]: number of days to countdown from (may be undefined)
 */
const RSACountdownRuleSyntax = /\{\s*(global_countdown|countdown)\s*\(\s*(?:(\d{4}-\d{1,2}-\d{1,2})(?:\s((?:[0-1][0-9]|2[0-3]):(?:[0-5][0-9]):(?:[0-5][0-9]))|))\s*(?:,\s*([1-9]\d{0,2})|)\s*\)\s*\}/i;

/*
 * Captures:
 * [0]: whole match
 * [1]: first letter of function name
 */
const countdownRuleInProgressSyntax = /\{=\s*(c|g)[a-z]*(?:\s*\([a-z0-9/":\s,-]*(?:\)\s*|)|)/i;

/*
 * Captures:
 * [0]: whole match
 * [1]: first letter of function name
 */
const RSACountdownRuleInProgressSyntax = /\{\s*(c(?:o|$)(?:u|$)|g(?:l|$)(?:o|$))[a-z_]*(?:\s*\([a-z0-9/":\s,-]*(?:\)\s*|)|)/i;

const coutndownRuleInProgressWithAdCustomizerSyntax = /\{=\s*(global_countdown|countdown)\s*\(((?:[^{}.\\]|(?:\\.)|\\[^.]?)*)\.((?:[^{}:\\]|(?:\\:)|\\[^:]?)*)?/i;

/*
 * Captures: Countdown feedname match
 *  Note: as feedname is too flexble which basically support everything
 *    we will just try to see whether it contains feedname.attributename format
 *    and leave others validation for MT
 * [0]: whole match
 * [1]: function name
 * [2]: feedname
 * [3]: attribute & other part
 * [4]: the '}' character - mark of a complete expression
 */
const countdownRuleWithAdCustomizerSyntax = /\{=\s*(global_countdown|countdown)\s*\(((?:[^{}.\\]|(?:\\.)|\\[^.]?)*)\.((?:[^{}:\\]|(?:\\:)|\\[^:]?)*)\)(?:(}))?/i;

/**
 * Looks for a countdown rule time definition part in given text starting from passed index.
 *   If found, it parses the definition.
 *  it will only capture the feedname as accurate
 *  then leave attributeWithOther part as one match (as feed name is too flexible)
 * @param {Object} options - options for the parse
 * @param {string} options.text - text to search in
 * @param {int} options.startIndex - starts searching at this index
 * @param {int} parseMode - flag for whether we are parsing for preview, editor or character count
 * @param {int} options.cursorIndex - current cursor position
 * @returns {Object} parses rule definition properties or undefined if a definiton is not found.
 */
function parseCountdownRuleUsingAdCustomizerFeedSyntax({
  text, startIndex, cursorIndex, i18n, config,
}) {
  const input = text.substring(startIndex);
  const tokens = countdownRuleWithAdCustomizerSyntax.exec(input);

  // Not an acceptable rule if syntax is invalid, no attribute name
  if ((tokens === null) ||
    tokens.length < 4 ||
    !tokens[3] ||
    (cursorIndex && cursorIndex <= (startIndex + tokens.index))) {
    return null;
  }

  // if it ends with ., then it is not valid
  return {
    // includeAdCustomizerFeed will be used by analyzer as editor will not shown when it is true
    includeAdCustomizerFeed: true,
    source: tokens[0],
    isComplete: !!tokens[4],
    type: tokens[1].toLowerCase(),
    feed: tokens[2],
    index: tokens.index,
    // as feed name/attribute name is too flexible, we use default langauge & default days here
    // for char counting purpose
    language: getDefaultLanguage(i18n, config),
    days: defaultCountdownDays,
  };
}

/**
 * Looks for a countdown rule definition in given text
 *   starting from passed index. If found, it parses the definition.
 * @param {string} text - text to search in
 * @param {int} startIndex - starts searching at this index
 * @param {int} parseMode - flag for whether we are parsing for preview, editor or character count
 * @param {int} cursorIndex - current cursor position
 * @returns {Object} parses rule definition properties or undefined if a definiton is not found.
 */


/**
 * Produces "<n> days" in current locale
 * @param {int} days - number of days
 * @param {Object} i18nDummy - i18n in a target countdown locale
 * @returns {string} "<n> days" in current locale
 */
function translateDays(days, i18nDummy) {
  const translation = i18nDummy.formatUnit(days, 'day');

  return translation;
}

/**
 * Looks for a countdown rule time definition part in given text starting from passed index.
 *   If found, it parses the definition.
 * @param {string} text - text to search in
 * @param {int} startIndex - starts searching at this index
 * @param {int} parseMode - flag for whether we are parsing for preview, editor or character count
 * @param {int} cursorIndex - current cursor position
 * @param {Object} config - config
 * @returns {Object} parses rule definition properties or undefined if a definiton is not found.
 */
export function parseCountdownRule({
  text, startIndex, parseMode, cursorIndex, config, i18n, adType = 'ExpandedTextAd',
}) {
  const { IsAdCustomizerEnabled } = config.get('Permissions');
  const input = text ? text.substring(startIndex) : '';

  if (adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd') {
    const tokens = RSACountdownRuleSyntax.exec(input);

    if ((tokens !== null) &&
      (tokens.length === 5) &&
      !Number.isNaN(Date.parse(tokens[2])) &&
      (!cursorIndex || (cursorIndex > (startIndex + tokens.index)))) {
      return {
        source: tokens[0],
        isComplete: true,
        type: tokens[1].toLowerCase(),
        date: tokens[2],
        time: tokens[3],
        days: tokens[4] ? parseInt(tokens[4], 10) : defaultCountdownDays,
        language: getDefaultLanguage(i18n, config),
        includeAdCustomizerFeed: false,
      };
    }
  } else {
    const tokens = countdownRuleSyntax.exec(input);

    // first try to parse countdown rule without adcustomizer
    // Not an acceptable rule if syntax is invalid, date is invalid, or has an unsupported language
    if ((tokens !== null) &&
      (tokens.length === 6) &&
      !Number.isNaN(Date.parse(tokens[2])) &&
      (!cursorIndex || (cursorIndex > (startIndex + tokens.index)))) {
      const language = getLanguage(tokens[4], parseMode, i18n, config);

      if (language) {
        return {
          source: tokens[0],
          isComplete: true,
          type: tokens[1].toLowerCase(),
          date: tokens[2],
          time: tokens[3],
          language,
          days: tokens[5] ? parseInt(tokens[5], 10) : defaultCountdownDays,
          includeAdCustomizerFeed: false,
        };
      }
    }

    // if countdown rule without ad customizer did not match
    // then try to parse it with adcustomizer feed
    if (IsAdCustomizerEnabled) {
      return parseCountdownRuleUsingAdCustomizerFeedSyntax({
        text: input, startIndex, cursorIndex, i18n, config,
      });
    }
  }

  return null;
}


/**
 * Looks for an incomplete countdown rule definition in given text
 *   starting from passed index. If found, it returns countdown type.
 * @param {Object} options needed
 * @returns {Object} parses rule definition properties or undefined if a definiton is not found.
 */
export function parseCountdownRuleInProgress({
  text, startIndex, cursorIndex, i18n, config, adType,
}) {
  const input = text.substring(startIndex);
  if (adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd') {
    const tokens = RSACountdownRuleInProgressSyntax.exec(input);

    if ((tokens === null) ||
      (cursorIndex && cursorIndex <= (startIndex + tokens.index))) {
      return null;
    }

    if (tokens.length !== 2 ||
      (!tokens[1].startsWith('c') && !tokens[1].startsWith('g'))) {
      return null;
    }

    return {
      source: tokens[0],
      type: tokens[1] === 'countdown(' || tokens[1].startsWith('c') ?
        'countdown' :
        'global_countdown',
      days: defaultCountdownDays,
    };
  }
  const tokens = countdownRuleInProgressSyntax.exec(input);

  if ((tokens === null) ||
    (cursorIndex && cursorIndex <= (startIndex + tokens.index))) {
    return null;
  }

  if ((tokens.length !== 2) ||
    ((tokens[1] !== 'c') && (tokens[1] !== 'g'))) {
    return null;
  }

  let includeAdCustomizerFeed = false;
  let source = tokens[0];

  // consider the countdown to contains feed after make sure it already contains the source
  const { IsAdCustomizerEnabled } = config.get('Permissions');
  if (source && IsAdCustomizerEnabled) {
    const matchedAdCustomizerTokens = coutndownRuleInProgressWithAdCustomizerSyntax.exec(input);

    if (matchedAdCustomizerTokens !== null) {
      includeAdCustomizerFeed = true;
      // eslint-disable-next-line prefer-destructuring
      source = matchedAdCustomizerTokens[0];
    }
  }

  return {
    source,
    type: tokens[1] === 'countdown(' || tokens[1] === 'c' ?
      'countdown' :
      'global_countdown',
    language: getDefaultLanguage(i18n, config),
    days: defaultCountdownDays,
    includeAdCustomizerFeed,
  };
}

/**
 * Looks for countdown rule definitions in given text
 *   and substitutes them with their evaluated and translated values.
 * @param {string} text - input text
 * @param {Object} i18n - i18n which has formatUnit function
 * @returns {object} a promise that resolves to a modified version of input text
 *   where all valid countdown rules
 * @param {Object} config - config
 * are substituted with their evaluated values
 */
export function evaluateCountdownRules({
  text, i18n, i18nDummy, config, adType = 'ExpandedTextAd',
}) {
  let processedText = text;

  function queueSubstitution(rule) {
    countdownEvalTaskChain = countdownEvalTaskChain.then((lang) => {
      const currentLanguage = lang.toLowerCase();

      if (rule.language.locale === currentLanguage) {
        const translation = translateDays(rule.days, i18nDummy);

        processedText = processedText.replace(rule.source, translation);
        return Promise.resolve(rule.language.locale);
      }
      return i18nDummy.setLocale(rule.language.locale).then(() => {
        const translation = translateDays(rule.days, i18nDummy);

        processedText = processedText.replace(rule.source, translation);
        return rule.language.locale;
      });
    });
  }

  let searchStartIndex = 0;
  let rule = parseCountdownRule({
    text,
    startIndex: searchStartIndex,
    parseMode: languageParseMode.ForPreview,
    i18n,
    config,
    adType,
  });

  // We need to use this global task chain to avoid interrupting
  //  ongoing get/set locale requests.
  // Each evaluation cycle begins with getting current locale and then
  //  for each countdown rule found,
  // we queue a task to set locale (if needed) and do substitution.
  if (rule !== null) {
    countdownEvalTaskChain = countdownEvalTaskChain.then(() => i18nDummy.getLocale());

    while (rule !== null) {
      queueSubstitution(rule);
      searchStartIndex += rule.source.length;
      rule = parseCountdownRule({
        text,
        startIndex: searchStartIndex,
        parseMode: languageParseMode.ForPreview,
        i18n,
        config,
        adType,
      });
    }
  }

  return countdownEvalTaskChain.then(() => processedText);
}

/**
 * Parses out properties to initialize a countdown helper using current cursor position
 * @param {string} text - input text
 * @param {int} cursorIndex - current cursor position
 * @param {Object} i18n - i18n which has formatUnit function
 * @param {Object} config - config
 * @returns {Object} properties to initialize a countdown helper
 *   if cursor is placed within a valid definition or at the end of a partial definition.
 * Returns undefined; otherwise.
 */
export function parseRule({
  text, cursorIndex, i18n, config, adType,
}) {
  const startIndex = text.lastIndexOf('{', cursorIndex);

  if (startIndex === -1 || cursorIndex === startIndex) {
    return null;
  }

  const parsers = [
    {
      // Check if cursor is placed in the middle of a valid syntax
      parse() {
        return parseCountdownRule({
          text,
          startIndex,
          parseMode: languageParseMode.ForEditor,
          cursorIndex,
          i18n,
          config,
          adType,
        });
      },
      keys: ['type', 'date', 'time', 'language', 'days', 'includeAdCustomizerFeed', 'feed', 'attributeWithOthers', 'isComplete', 'source'],
      inclusive: false,
    },
    {
      // Check if we are, instead, just starting to type a countdown rule
      parse() {
        return parseCountdownRuleInProgress({
          text,
          startIndex,
          cursorIndex,
          i18n,
          config,
          adType,
        });
      },
      keys: ['type', 'language', 'days', 'includeAdCustomizerFeed', 'source'],
      inclusive: true,
    },
  ];

  let properties = null;

  some(parsers, (parser) => {
    const rule = parser.parse();

    if ((rule !== null) &&
      (cursorIndex < startIndex + rule.source.length + (parser.inclusive ? 1 : 0))) {
      const options = pick(rule, parser.keys);

      options.startIndex = startIndex;
      options.endIndex = startIndex + rule.source.length;

      properties = {
        type: 'countdown',
        options,
      };

      return true;
    }
    return false;
  });

  return properties;
}
